import { hasSuperUserAccess } from './sessionUser';
import { isFirstDegreeOrHigher, viewerGradeRank } from './gradeAccess';
import { normalizeGradeName } from './syllabusGradeIcons';

export const GRADING_TILE_USERNAMES = ['Eamon', 'Bren', 'Frank', 'Ciaran'];

function usernameMatches(user, name) {
  return String(user?.username || '').trim().toLowerCase() === String(name).trim().toLowerCase();
}

export function isNamedGradingTileUser(user) {
  if (!user) return false;
  return GRADING_TILE_USERNAMES.some((name) => usernameMatches(user, name));
}

/** Builders → Grading tile visibility. */
export function canSeeGradingTile(user) {
  if (!user) return false;
  if (isNamedGradingTileUser(user)) return true;
  return isFirstDegreeOrHigher(user);
}

/** Bren or true super user — may set any student to any grade. */
export function canChangeAnyStudentGrade(user) {
  if (!user) return false;
  if (hasSuperUserAccess(user)) return true;
  return usernameMatches(user, 'Bren');
}

function gradesMatchName(a, b) {
  return normalizeGradeName(a) === normalizeGradeName(b);
}

/** Single current grade from catalog; `student_grade` name wins over stale `student_grade_rank`. */
export function resolveStudentCurrentGrade(student, allGrades) {
  if (!student || !Array.isArray(allGrades)) return null;
  if (student.student_grade) {
    const byName = allGrades.find((g) => gradesMatchName(g.grade_name, student.student_grade));
    if (byName) return byName;
  }
  const rank = student.student_grade_rank;
  if (rank != null && !Number.isNaN(Number(rank))) {
    return allGrades.find((g) => Number(g.grade_rank_order) === Number(rank)) ?? null;
  }
  return null;
}

export function resolveStudentGradeRank(student, allGrades) {
  if (!student) return null;
  const current = resolveStudentCurrentGrade(student, allGrades);
  if (current?.grade_rank_order != null) {
    return Number(current.grade_rank_order);
  }
  if (student.student_grade_rank != null && !Number.isNaN(Number(student.student_grade_rank))) {
    return Number(student.student_grade_rank);
  }
  return viewerGradeRank(student);
}

export function canSelectStudentForGrading(viewer, student, allGrades) {
  if (!viewer || !student) return false;
  if (canChangeAnyStudentGrade(viewer)) return true;
  if (!isFirstDegreeOrHigher(viewer)) return false;
  const viewerRank = viewerGradeRank(viewer);
  const studentRank = resolveStudentGradeRank(student, allGrades);
  if (viewerRank == null) return false;
  if (studentRank == null) return true;
  return studentRank < viewerRank;
}

/**
 * Grades the viewer may assign to the selected student (upgrade only unless full access).
 * @param {import('./sessionUser').StudentUser|null|undefined} viewer
 * @param {object|null|undefined} targetStudent
 * @param {Array<{ grade_name: string, grade_rank_order?: number|null }>} allGrades
 */
export function getAssignableGradesForStudent(viewer, targetStudent, allGrades) {
  if (!viewer || !targetStudent || !Array.isArray(allGrades)) return [];
  if (canChangeAnyStudentGrade(viewer)) return allGrades;

  if (!isFirstDegreeOrHigher(viewer)) return [];

  const viewerRank = viewerGradeRank(viewer);
  const studentRank = resolveStudentGradeRank(targetStudent, allGrades);
  if (viewerRank == null) return [];
  if (studentRank != null && studentRank >= viewerRank) return [];

  const effectiveStudentRank = studentRank ?? -1;

  return allGrades.filter((g) => {
    const r = g.grade_rank_order;
    if (r == null || Number.isNaN(Number(r))) return false;
    const rank = Number(r);
    return rank > effectiveStudentRank && rank < viewerRank;
  });
}

export function isStudentCurrentGrade(student, grade, allGrades) {
  const current = resolveStudentCurrentGrade(student, allGrades);
  if (!current || !grade) return false;
  if (current.id != null && grade.id != null) {
    return Number(current.id) === Number(grade.id);
  }
  return gradesMatchName(current.grade_name, grade.grade_name);
}
